import { Component, OnInit, OnDestroy } from '@angular/core';
import { IReportMachineStatusModal, IReportByAllStatus, IStatusFamily } from '../../../interfaces/machine-status';
import { Subscription } from 'rxjs';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { groupBy } from 'projects/common/src/app/services/utils/utilities';
import * as moment from 'moment';
import { LanguageService } from 'projects/common/src/app/services/parameters';
import { ITableDataParams } from 'projects/common/src/app/interfaces/table/table-data-params';
import { ITableExtraParams } from 'projects/common/src/app/interfaces/table/table-extra-params';
import { ITableHeaderParams } from 'projects/common/src/app/interfaces/table/table-header-params';
import { ITableConfig } from '../../../../../../../projects/common/src/app/interfaces/table/table-config';
import { TableConfigService } from '../../../../../../../projects/common/src/app/services/table/table-config.service';
import { ITableLargeColumnConfig } from '../../../../../../../projects/common/src/app/interfaces/table/table-large-column-config';
import { AuthenticationService } from 'projects/authentication/src/app/services';

@Component({
  selector: 'app-substate-modal',
  templateUrl: './substate-modal.component.html',
  styleUrls: ['./substate-modal.component.scss'],
})
export class SubstateModalComponent implements OnInit, OnDestroy {
  public subSource: Subscription;

  public modalData: { selectedStatus: IStatusFamily; allStatus: IReportByAllStatus[] };
  public familyStatusName: string;
  public statusName: string;
  public duration: string;
  public color: string;
  public tableConfig: ITableConfig;
  public logoutSub: Subscription;

  constructor(
    private activeModal: NgbActiveModal,
    private languageService: LanguageService,
    private tableConfigService: TableConfigService,
    private authService: AuthenticationService,
  ) {}

  /**
   * On init component, define properties & init table data
   */
  public ngOnInit(): void {
    this.familyStatusName = this.modalData.selectedStatus.statusFamilyName;
    this.statusName = this.modalData.selectedStatus.statusName;
    this.duration = this.modalData.selectedStatus.durationTime;
    this.color = this.modalData.selectedStatus.statusFamilyColor;

    this.initTableData();
    this.logoutSub = this.authService.tokenExpired$.subscribe(() => {
      this.activeModal.dismiss('logout');
    });
  }

  /**
   * Function to draw table
   */
  private initTableData(): void {
    const substaus: IReportByAllStatus[] = this.modalData.allStatus.filter(
      // filter by substatus
      (status) => this.modalData.selectedStatus.statusId === status.statusId,
    );
    let totalDuration = 0;
    substaus.forEach((substate) => {
      totalDuration += substate.durationSec;
      return totalDuration;
    }); // sum all seconds

    const grupedSubstatus = groupBy(substaus, (s) => s.machineId); // group by machineId

    const substatusDetail: IReportMachineStatusModal[] = [];

    // eslint-disable-next-line guard-for-in
    for (const key in grupedSubstatus) {
      let totalByMachine = 0;
      grupedSubstatus[key].map((status) => {
        totalByMachine += status.durationSec;
      });

      const detail: IReportMachineStatusModal = {
        machine: grupedSubstatus[key][0].machineName,
        statusName: grupedSubstatus[key][0].statusName,
        percentage: `${((totalByMachine * 100) / totalDuration).toFixed(2)}%`,
        percentageSec: totalByMachine,
        duration: this.formatSecondsToDurationFormat(totalByMachine),
        durationSec: grupedSubstatus[key][0].durationSec,
      };

      substatusDetail.push(detail);
    }

    this.setTableConfig(substatusDetail);
  }

  /**
   * Function to set table config
   *
   * @param tableData Data to fill table
   */
  private setTableConfig(tableData: any[]): void {
    const excludeColumns = ['statusName', 'durationSec', 'percentageSec'];
    const customLargeColumns: ITableLargeColumnConfig[] = [
      { field: 'machine', isLargeColumn: true, flexGrow: 0.8 },
      { field: 'percentage', isLargeColumn: true, flexGrow: 0.5 },
      { field: 'duration', isLargeColumn: true, flexGrow: 1.7 },
    ];

    const headerParams: ITableHeaderParams = {
      data: tableData,
      replaceColumnName: [
        { prevName: 'machine', newName: 'machineType' },
        { prevName: 'percentage', newName: 'percentSymbol' },
        { prevName: 'duration', newName: 'time' },
      ],
      sortables: true,
      excludeColumns,
      customLargeColumns,
    };
    const datasetParams: ITableDataParams = {
      tableData,
      colorConfig: [{ field: 'machine', isBold: true }],
      excludeColumns,
      customLargeColumns,
    };
    const header = this.tableConfigService.setHeaderConfig(headerParams);
    const dataset = this.tableConfigService.setDatasetConfig(datasetParams);

    const extraParams: ITableExtraParams = {
      noDataMsg: 'm019',
      disableFooter: true,
      minimalRows: tableData.length,
      quantityPerPage: tableData.length,
    };

    this.tableConfig = { header, dataset, extraParams };
  }

  /**
   * Format seconds to (Hrs Min Seg)
   *
   * @param seconds seconds
   */
  public formatSecondsToDurationFormat(seconds: number): string {
    const duration = moment.duration(seconds, 'seconds');

    const daysTohours = duration.days() * 24; // convert days to hours
    const hours = duration.hours() + daysTohours;

    let format = '';
    format += hours ? `${hours} Hr. ` : ''; // Hrs
    format += duration.minutes() ? `${duration.minutes()} Min. ` : ''; // Min
    format += duration.seconds() ? `${duration.seconds()} ${this.languageService.message('seconds_short')}` : '';
    return format;
  }

  /**
   * Close  modal
   */
  public close(): void {
    this.activeModal.dismiss();
  }

  /**
   * On destroy component
   */
  public ngOnDestroy(): void {
    if (this.subSource) {
      this.subSource.unsubscribe();
    }
    // Unsubscribe to avoid memory leaks
    if (this.logoutSub) {
      this.logoutSub.unsubscribe();
    }
  }
}
